import * as THREE from 'three';
import { Pool } from '../engine/pool.js';

// Projectiles are billboarded elongated cubes. Pool of 256 total, split logically
// by `team` at runtime (0 = player, 1 = enemy).
//
// Each projectile gets its OWN material instance so the game loop can modulate
// color/opacity per-bullet based on the player's light radius. `baseColor` and
// `baseOpacity` hold the intended look; the renderer step multiplies by an
// illumination factor each frame.

const TEMPLATES = {
  player: { color: 0x8afff0, opacity: 1.0 },
  enemy: { color: 0xff4060, opacity: 1.0 },
  echo: { color: 0x70e0ff, opacity: 0.7 },
};

export function createProjectilePool(scene, poolSize = 256) {
  const coreGeometry = new THREE.BoxGeometry(0.12, 0.12, 0.6);
  const trailGeometry = new THREE.ConeGeometry(0.12, 1.7, 9, 1, true);
  const glowGeometry = new THREE.IcosahedronGeometry(0.16, 1);
  const outerGlowGeometry = new THREE.IcosahedronGeometry(0.3, 1);

  const pool = new Pool(() => {
    const material = new THREE.MeshBasicMaterial({
      color: TEMPLATES.player.color,
      transparent: true,
      opacity: TEMPLATES.player.opacity,
    });
    material.depthWrite = false;

    const mesh = new THREE.Mesh(coreGeometry, material);
    mesh.renderOrder = 12;

    const trailMaterial = new THREE.MeshBasicMaterial({
      color: TEMPLATES.player.color,
      transparent: true,
      opacity: 0.42,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const trail = new THREE.Mesh(trailGeometry, trailMaterial);
    trail.rotation.x = -Math.PI * 0.5;
    trail.position.z = -0.96;
    trail.renderOrder = 10;
    mesh.add(trail);

    const glowMaterial = new THREE.MeshBasicMaterial({
      color: TEMPLATES.player.color,
      transparent: true,
      opacity: 0.36,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.renderOrder = 11;
    mesh.add(glow);

    const outerGlowMaterial = new THREE.MeshBasicMaterial({
      color: TEMPLATES.player.color,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
    });
    const outerGlow = new THREE.Mesh(outerGlowGeometry, outerGlowMaterial);
    outerGlow.renderOrder = 9;
    mesh.add(outerGlow);

    mesh.visible = false;
    scene.add(mesh);
    return {
      mesh,
      trail,
      glow,
      outerGlow,
      alive: false,
      team: 0,
      x: 0, z: 0, dx: 0, dz: 0,
      speed: 0,
      life: 0,
      age: 0,
      damage: 1,
      critMul: 1,
      maxBounces: 0,
      bounceCount: 0,
      echo: false,
      sourceWeaponId: null,
      explosionRadius: 0,
      explosionDamage: 0,
      clusterCount: 0,
      delayedFuse: 0,
      bleedDps: 0,
      bleedDuration: 0,
      baseColor: new THREE.Color(TEMPLATES.player.color),
      baseOpacity: TEMPLATES.player.opacity,
      trailOpacity: 0.42,
      glowOpacity: 0.36,
      outerGlowOpacity: 0.16,
      isBossShot: false,
    };
  }, (p) => {
    p.alive = true;
    p.team = 0;
    p.x = 0; p.z = 0;
    p.dx = 0; p.dz = 0;
    p.speed = 20;
    p.life = 1.2;
    p.age = 0;
    p.damage = 1;
    p.critMul = 1;
    p.maxBounces = 0;
    p.bounceCount = 0;
    p.echo = false;
    p.sourceWeaponId = null;
    p.explosionRadius = 0;
    p.explosionDamage = 0;
    p.clusterCount = 0;
    p.delayedFuse = 0;
    p.bleedDps = 0;
    p.bleedDuration = 0;
    p.isBossShot = false;
    p.mesh.visible = true;
    p.mesh.scale.set(1, 1, 1);
    p.trail.scale.set(1, 1, 1);
    p.glow.scale.set(1, 1, 1);
    p.outerGlow.scale.set(1, 1, 1);
    p.baseColor.setHex(TEMPLATES.player.color);
    p.baseOpacity = TEMPLATES.player.opacity;
    p.trailOpacity = 0.42;
    p.glowOpacity = 0.36;
    p.outerGlowOpacity = 0.16;
    p.mesh.material.color.copy(p.baseColor);
    p.mesh.material.opacity = p.baseOpacity;
    p.trail.material.color.copy(p.baseColor);
    p.trail.material.opacity = p.trailOpacity;
    p.glow.material.color.copy(p.baseColor);
    p.glow.material.opacity = p.glowOpacity;
    p.outerGlow.material.color.copy(p.baseColor);
    p.outerGlow.material.opacity = p.outerGlowOpacity;
  }, poolSize);

  pool._templates = TEMPLATES;
  return pool;
}

export function configureProjectile(p, {
  team,
  x,
  z,
  dx,
  dz,
  speed,
  damage,
  life,
  critMul,
  maxBounces,
  echo,
  sourceWeaponId,
  explosionRadius,
  explosionDamage,
  clusterCount,
  delayedFuse,
  bleedDps,
  bleedDuration,
  isBossShot,
}) {
  p.team = team;
  p.x = x; p.z = z;
  p.dx = dx; p.dz = dz;
  p.speed = speed;
  p.damage = damage;
  p.life = life;
  p.critMul = critMul ?? 1;
  p.maxBounces = maxBounces ?? 0;
  p.bounceCount = 0;
  p.echo = !!echo;
  p.sourceWeaponId = sourceWeaponId ?? null;
  p.explosionRadius = explosionRadius ?? 0;
  p.explosionDamage = explosionDamage ?? 0;
  p.clusterCount = clusterCount ?? 0;
  p.delayedFuse = delayedFuse ?? 0;
  p.bleedDps = bleedDps ?? 0;
  p.bleedDuration = bleedDuration ?? 0;
  p.isBossShot = !!isBossShot;

  const tpl = echo ? TEMPLATES.echo : (team === 0 ? TEMPLATES.player : TEMPLATES.enemy);
  p.baseColor.setHex(tpl.color);
  p.baseOpacity = tpl.opacity;
  p.mesh.material.color.copy(p.baseColor);
  p.mesh.material.opacity = p.baseOpacity;
  p.trail.material.color.copy(p.baseColor);
  p.glow.material.color.copy(p.baseColor);
  p.trailOpacity = Math.max(0.28, p.baseOpacity * 0.46);
  p.glowOpacity = Math.max(0.24, p.baseOpacity * 0.38);
  p.outerGlowOpacity = Math.max(0.1, p.baseOpacity * 0.16);
  p.trail.material.opacity = p.trailOpacity;
  p.glow.material.opacity = p.glowOpacity;
  p.outerGlow.material.opacity = p.outerGlowOpacity;
  p.age = 0;

  p.mesh.position.set(x, 1.05, z);
  p.mesh.rotation.y = Math.atan2(dx, dz);
}
